// Task #4277/#4278 — Contratto BE↔FE degli avvisi automatici scorte e Logistica (POS).
// Consumato da OversoldSalesPanel.tsx (FE) e reports-analytics.routes.ts /
// pos-stock-alerts.service.ts (BE).
import { z } from 'zod';

/**
 * Modalità destinatari in-app:
 * - `sector_responsibles` (default): responsabili del settore Logistica delle
 *   sedi del magazzino coinvolto; i ruoli configurati restano fallback per le
 *   sedi/magazzini senza responsabile (nessun allarme perso).
 * - `fixed_roles`: solo gli utenti attivi con uno dei ruoli configurati.
 */
export const POS_STOCK_ALERT_RECIPIENT_MODES = ['sector_responsibles', 'fixed_roles'] as const;
export const posStockAlertRecipientModeSchema = z.enum(POS_STOCK_ALERT_RECIPIENT_MODES);
export type PosStockAlertRecipientMode = z.infer<typeof posStockAlertRecipientModeSchema>;

/** GET /pos/stock-alerts/settings — riga pos_stock_alert_settings (o default). */
export const posStockAlertSettingsSchema = z.object({
  tenant_id: z.string(),
  oversold_enabled: z.boolean(),
  low_stock_enabled: z.boolean(),
  notify_in_app: z.boolean(),
  notify_email: z.boolean(),
  recipient_roles: z.array(z.string()),
  recipient_mode: posStockAlertRecipientModeSchema,
  email_recipients: z.string().nullable(),
  daily_summary_hour: z.number().int(),
  // Task #4278 — avvisi Logistica (pg numeric arriva come stringa: coerce).
  receipt_discrepancy_enabled: z.boolean(),
  inventory_variance_enabled: z.boolean(),
  inventory_variance_qty_threshold: z.coerce.number().min(0),
  inventory_variance_value_threshold: z.coerce.number().min(0),
  supplier_import_failed_enabled: z.boolean(),
  transfer_stale_enabled: z.boolean(),
  transfer_stale_hours: z.number().int().min(1).max(720),
});
export type PosStockAlertSettings = z.infer<typeof posStockAlertSettingsSchema>;

/** PUT /pos/stock-alerts/settings — patch parziale, strict (niente colonne fantasma). */
export const posStockAlertSettingsUpdateSchema = z.object({
  oversold_enabled: z.boolean().optional(),
  low_stock_enabled: z.boolean().optional(),
  notify_in_app: z.boolean().optional(),
  notify_email: z.boolean().optional(),
  recipient_roles: z.array(z.string().min(1).max(100)).max(50).optional(),
  recipient_mode: posStockAlertRecipientModeSchema.optional(),
  email_recipients: z.string().max(4000).nullable().optional(),
  daily_summary_hour: z.number().int().min(0).max(23).optional(),
  receipt_discrepancy_enabled: z.boolean().optional(),
  inventory_variance_enabled: z.boolean().optional(),
  inventory_variance_qty_threshold: z.number().min(0).max(999999).optional(),
  inventory_variance_value_threshold: z.number().min(0).max(99999999).optional(),
  supplier_import_failed_enabled: z.boolean().optional(),
  transfer_stale_enabled: z.boolean().optional(),
  transfer_stale_hours: z.number().int().min(1).max(720).optional(),
}).strict();
export type PosStockAlertSettingsUpdate = z.infer<typeof posStockAlertSettingsUpdateSchema>;

/** Riga sotto scorta per (magazzino, articolo) — GET /pos/stock-alerts/low-stock. */
export const posLowStockItemSchema = z.object({
  product_id: z.string(),
  product_name: z.string(),
  sku: z.string().nullable(),
  warehouse_id: z.string(),
  warehouse_name: z.string(),
  quantity_available: z.number(),
  reorder_point: z.number(),
  min_stock_level: z.number().nullable(),
});
export type PosLowStockItem = z.infer<typeof posLowStockItemSchema>;

/** POST /pos/stock-alerts/run-daily-summary */
export const posLowStockSummaryRunResultSchema = z.object({
  sent: z.boolean(),
  items: z.number().int(),
  recipients: z.number().int(),
  reason: z.string().optional(),
});
export type PosLowStockSummaryRunResult = z.infer<typeof posLowStockSummaryRunResultSchema>;
